import type { ManagementTimelineQuery } from '@internal/spassu-code-contract';

export interface TimelineFilterDraft {
  type: string;
  outcome: string;
  skill: string;
  from: string;
  to: string;
  attention: string;
  groupBy: string;
}

export function toTimelineFilterDraft(
  query: ManagementTimelineQuery,
): TimelineFilterDraft {
  return {
    type: query.type ?? '',
    outcome: query.outcome ?? '',
    skill: query.skill ?? '',
    from: toLocalDateTime(query.from),
    to: toLocalDateTime(query.to),
    attention: query.attention === undefined ? '' : String(query.attention),
    groupBy: query.groupBy ?? '',
  };
}

export function applyTimelineFilterDraft(
  query: ManagementTimelineQuery,
  draft: TimelineFilterDraft,
): ManagementTimelineQuery {
  const period = normalizedPeriod(draft);
  return {
    ...query,
    type: draft.type
      ? (draft.type as ManagementTimelineQuery['type'])
      : undefined,
    outcome: draft.outcome
      ? (draft.outcome as ManagementTimelineQuery['outcome'])
      : undefined,
    skill: draft.skill.trim() || undefined,
    ...period,
    attention: draft.attention === '' ? undefined : draft.attention === 'true',
    groupBy: draft.groupBy
      ? (draft.groupBy as ManagementTimelineQuery['groupBy'])
      : undefined,
    groupKey: undefined,
    page: 1,
  };
}

function normalizedPeriod(
  draft: TimelineFilterDraft,
): Pick<ManagementTimelineQuery, 'from' | 'to'> {
  const from = toIsoDateTime(draft.from);
  const to = toIsoDateTime(draft.to);
  if (from && to && Date.parse(from) > Date.parse(to)) {
    return { from: undefined, to: undefined };
  }
  return { from, to };
}

export function clearTimelineFilters(
  query: ManagementTimelineQuery,
): ManagementTimelineQuery {
  return { view: query.view, page: 1, limit: query.limit };
}

export function changeTimelineView(
  query: ManagementTimelineQuery,
  view: 'management' | 'technical',
): ManagementTimelineQuery {
  return { ...clearTimelineGroup(query), view, page: 1 };
}

export function expandTimelineGroup(
  query: ManagementTimelineQuery,
  groupKey: string,
): ManagementTimelineQuery {
  return { ...query, groupKey, page: 1 };
}

export function clearTimelineGroup(
  query: ManagementTimelineQuery,
): ManagementTimelineQuery {
  const next = { ...query };
  delete next.groupKey;
  return { ...next, page: 1 };
}

function toLocalDateTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function toIsoDateTime(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
